/**
 * HR Command Center — functional dashboard for Human Resources.
 *
 * Live headcount by role + open access requests + IT authorizations
 * (dual approval) + dept inbox + quick actions. Turns HR from a
 * placeholder page into a working cockpit.
 */
import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Users, UserPlus, ClipboardCheck, Shield, GraduationCap, ArrowRightLeft } from 'lucide-react';
import { KpiTile, DeptInbox, RouteTaskDialog } from './DeptTaskShared';
import DeptQuickAccess from './DeptQuickAccess';
import ITAuthorizationsPanel from './ITAuthorizationsPanel';
import HRSundayOverridePanel from './HRSundayOverridePanel';
import { isAdminRow } from '../shared/MetroKindHeader';
import UnifiedGeoRoleFilter from '../UnifiedGeoRoleFilter';
import { DepartmentDetails } from './DepartmentDetails';
import { DEPT_BY_KEY } from './AdminDepartments';
// Iter-84 — HR gets the same real-time hiring/retention scorecard the CEO sees.
import { GeoFilterProvider, GeoFilterBar, HrLeadershipPanel } from './CeoStrategyPanels';
// Iter-157 · Phase 3 — HR gets the four distinct per-property tabs.
import DeptTabBoard from './DeptTabBoard';
import { Receipt, FileCheck, DollarSign, ClipboardList } from 'lucide-react';
import { BillingARBoard, PropertiesPricingBoard, PropertiesContractsBoard, PropertiesServicesAgreementBoard } from './PropertiesBoards';

const HR_PROPERTY_TABS = [
  { key: 'billing-ar',            label: 'Billing & A/R',              icon: Receipt,       render: () => <BillingARBoard /> },
  { key: 'properties-pricing',    label: 'Properties Pricing',         icon: DollarSign,    render: () => <PropertiesPricingBoard /> },
  { key: 'properties-contracts',  label: 'Properties Contracts',       icon: FileCheck,     render: () => <PropertiesContractsBoard /> },
  { key: 'properties-services',   label: 'Properties & Services Agreement', icon: ClipboardList, render: () => <PropertiesServicesAgreementBoard /> },
];

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_COUNT_CONFIG = [
  { role: 'company_manager', label: 'NECS Admins', tone: '#FFD60A' },
  { role: 'supervisor', label: 'Supervisors', tone: '#0ea5e9' },
  { role: 'crew_leader', label: 'Crew Leaders', tone: '#22c55e' },
  { role: 'agent', label: 'Site Agents', tone: '#a855f7' },
];

export default function HRCommandCenter({ onOpenSection, boardFilter }) {
  const [employees, setEmployees] = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  const [route, setRoute] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // Section 2 (iter-138) — append pivot filter to snapshot fetches
        // so HR headcount + access-requests reflect the selected scope.
        const qs = boardFilter?.queryString ? `?${boardFilter.queryString}` : '';
        const [emps, ar] = await Promise.all([
          fetch(`${API}/api/company/employees${qs}`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : [])),
          fetch(`${API}/api/access-requests${qs}`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : [])),
        ]);
        setEmployees(Array.isArray(emps) ? emps : []);
        setAccessRequests(Array.isArray(ar) ? ar : []);
      } catch (_) { /* noop */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardFilter?.queryString, boardFilter?.refreshTick]);

  const totalHeadcount = employees.length;
  const roleCount = (r) => employees.filter((u) => u.role === r).length;
  const adminCount = employees.filter(isAdminRow).length;
  const staffCount = employees.length - adminCount;
  const pendingAR = accessRequests.filter((r) => r.status === 'pending').length;
  const approvedThisWeek = accessRequests.filter((r) => {
    if (r.status !== 'approved') return false;
    if (!r.reviewed_at) return false;
    const d = new Date(r.reviewed_at);
    return (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24) <= 7;
  }).length;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-6" data-testid="hr-command-center">
      {/* Header */}
      <div
        className="rounded-2xl p-4 sm:p-6 border-2 flex flex-wrap items-start gap-4"
        style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 60%, #2D2D2D 100%)', borderColor: 'rgba(201,169,98,0.5)' }}
        data-testid="hr-hero"
      >
        <div className="h-16 w-16 rounded-xl flex items-center justify-center"
             style={{ backgroundColor: 'rgba(201,169,98,0.15)', border: '2px solid #C9A962' }}>
          <Users className="h-8 w-8 text-[#C9A962]" />
        </div>
        <div className="flex-1 min-w-0 basis-48">
          <p className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[#C9A962]">
            Human Resources · Command Center · HRX
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mt-1"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.02em' }}>
            HR HEADQUARTERS
          </h1>
          <p className="text-xs text-zinc-400 mt-3 max-w-3xl leading-relaxed">
            Hire, develop, and protect the people who build Noble Empire. Live headcount, access
            reviews, and cross-department requests from Operations flow through this cockpit.
          </p>
        </div>
        <Button onClick={() => setRoute(true)} className="bg-[#C9A962] hover:bg-[#EAB308] text-black font-bold"
                data-testid="hr-route-btn">
          <ArrowRightLeft className="h-4 w-4 mr-2" /> Route Work
        </Button>
      </div>

      {/* Big City + Admin/Field slicer — CEO's platform-wide filter */}

      {/* Iter-111 · Enterprise Pivot Filter — below header + slicer. */}
      <DepartmentDetails dept={DEPT_BY_KEY.hr} prefix="hr" />
      {boardFilter && <UnifiedGeoRoleFilter filter={boardFilter} testIdPrefix="unified-filter-hr" />}
      <div className="flex flex-wrap gap-4"><a href="/attendance" className="text-[#C9A962] underline" data-testid="hr-attendance-workspace">Attendance reviews</a><a href="/workforce-payroll" className="text-[#C9A962] underline" data-testid="hr-payroll-workspace">Start cycle & authorize payroll</a><a href="/rewards-review" className="text-[#C9A962] underline" data-testid="hr-six-month-rewards-link">Monthly progress & six-month payroll reward review</a></div>
      <a href="/time-corrections" className="inline-block text-[#C9A962] underline text-sm" data-testid="hr-time-corrections">HR time corrections</a>
      <a href="/pay-rates" className="inline-block text-[#C9A962] underline text-sm" data-testid="hr-shared-pay-rates">Shared Valet & Warehouse pay rates</a>

      {/* Quick Access strip — HR-owned operational areas
          (Employees, Attendance, Blackouts, Properties Pricing +
          Contracts). Clicking a tile flips the top-level Tabs to the
          section content and remembers HR as the parent for the
          breadcrumb. */}

      {/* KPI tiles */}
      <section>
        <h2 className="text-xs uppercase tracking-widest text-[#C9A962] mb-3 font-semibold">Live Workforce · Payroll Roster</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <KpiTile icon={<Users className="h-5 w-5" />} label="Payroll headcount" value={totalHeadcount}
                   sub="All NECS-employed roles" tone="#C9A962" testid="hr-kpi-total" />
          {ROLE_COUNT_CONFIG.map((cfg) => (
            <KpiTile
              key={cfg.role}
              icon={<Users className="h-5 w-5" />}
              label={cfg.label}
              value={roleCount(cfg.role)}
              sub=""
              tone={cfg.tone}
              testid={`hr-kpi-${cfg.role}`}
            />
          ))}
        </div>
      </section>

      {/* Access Requests overview */}
      <section>
        <h2 className="text-xs uppercase tracking-widest text-[#C9A962] mb-3 font-semibold">Access & Onboarding</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <KpiTile icon={<ClipboardCheck className="h-5 w-5" />} label="Pending access requests"
                   value={pendingAR}
                   sub="From property managers or new hires"
                   tone={pendingAR > 0 ? '#ef4444' : '#22c55e'}
                   testid="hr-kpi-pending-ar" />
          <KpiTile icon={<UserPlus className="h-5 w-5" />} label="Approved · last 7 days"
                   value={approvedThisWeek} sub="New accounts provisioned" tone="#22c55e"
                   testid="hr-kpi-recent-approved" />
          <KpiTile icon={<GraduationCap className="h-5 w-5" />} label="Onboarding cohorts"
                   value="—" sub="Course wiring pending" tone="#71717a"
                   testid="hr-kpi-onboarding" />
        </div>
      </section>

      {/* Recent access requests preview */}
      <DeptQuickAccess deptKey="hr" accent="#C9A962" onOpen={onOpenSection} />
      <Card className="bg-zinc-900 border-zinc-700 p-5" data-testid="hr-access-requests-panel">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-white flex items-center gap-2"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <ClipboardCheck className="h-5 w-5 text-[#C9A962]" /> RECENT ACCESS REQUESTS
          </h3>
          <Badge className="bg-[#C9A962] text-black">{accessRequests.length}</Badge>
        </div>
        {accessRequests.length === 0 ? (
          <p className="text-xs text-zinc-500 py-4 text-center">No access requests filed yet.</p>
        ) : (
          <ul className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
            {accessRequests.slice(0, 8).map((r) => (
              <li key={r.request_id || r.id} className="bg-zinc-800 border border-zinc-700 rounded p-3 text-xs">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-semibold">{r.full_name || r.email}</p>
                    <p className="text-zinc-400 text-[11px]">
                      {r.kind || 'access request'} · {r.email} · {r.property_name || r.region || ''}
                    </p>
                  </div>
                  <Badge className={r.status === 'pending' ? 'bg-amber-500 text-black'
                                    : r.status === 'approved' ? 'bg-emerald-600 text-white'
                                    : 'bg-zinc-600 text-white'}>
                    {(r.status || 'pending').toUpperCase()}
                  </Badge>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Dept inbox */}
      <DeptInbox deptKey="hr" title="HR TASK INBOX" />

      {/* Iter-84 · HR scorecard for the department landing — same panel the
          CEO sees, but rendered here so directors get the same live view. */}
      <GeoFilterProvider>
        <div className="pt-2 space-y-6" data-testid="hr-dept-scorecard">
          <GeoFilterBar />
          <HrLeadershipPanel />
        </div>
      </GeoFilterProvider>

      {/* IT Access Authorization (HR arm of the dual-approval) */}
      <div className="pt-2">
        <h3 className="text-xs uppercase tracking-widest text-[#C9A962] mb-3 font-semibold flex items-center gap-2">
          <Shield className="h-4 w-4" /> IT Cross-Access Authorizations (HR side)
        </h3>
        <ITAuthorizationsPanel officerRole="hr" />
      </div>

      {/* Section 6 (iter-130) · HR Sunday & Holiday override toggle. */}
      <HRSundayOverridePanel />

      {/* Iter-157 · Phase 3 — HR tab-and-board with the four distinct
          per-property tabs (Billing & A/R, Properties Pricing,
          Properties Contracts, Properties & Services Agreement). */}
      <DeptTabBoard
        testidPrefix="hr-tab-board"
        accent="#0ea5e9"
        tabs={HR_PROPERTY_TABS}
      />

      <RouteTaskDialog open={route} onClose={() => setRoute(false)} onSaved={() => window.location.reload()} defaultDept="hr" />
    </div>
  );
}
